/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type StockPublicDataFragment } from '../../../../api';
import { type MobilityApplicationEntrypointContextDataFragment } from '../../../../api/fragments/MobilityApplicationEntrypointContextData';
import {
    type DraftGiftVoucherMutation,
    type DraftGiftVoucherMutationVariables,
    DraftGiftVoucherDocument,
} from '../../../../api/mutations/draftGiftVoucher';
import { useThemeComponents } from '../../../../themes';
import { JourneyStage } from '../../FinderApplicationPublicAccessEntrypoint/types';

const useSubmitGiftVoucher = (
    stock: Extract<StockPublicDataFragment, { __typename: 'MobilityStockInventory' }>,
    endpoint: MobilityApplicationEntrypointContextDataFragment,
    currentLanguageId: string
) => {
    const { t } = useTranslation(['carDetails']);
    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    const { notification } = useThemeComponents();

    return useCallback(async () => {
        notification.loading({
            content: t('carDetails:messages.draftingGiftCode'),
            duration: 0,
            key: 'primary',
        });
        const { data } = await apolloClient
            .mutate<DraftGiftVoucherMutation, DraftGiftVoucherMutationVariables>({
                mutation: DraftGiftVoucherDocument,
                variables: {
                    setting: {
                        customer: {
                            newLocalCustomer: { fields: [] },
                        },
                        dealerId: stock.inventory.__typename === 'MobilityInventory' && stock.inventory.dealer.id,
                        inventoryId: stock.inventory.id,
                        mobilityModuleId: endpoint.mobilityApplicationModule.id,
                        moduleId: endpoint.mobilityApplicationModule.giftVoucherModuleId,
                        endpointId: endpoint.id,
                        vehicle: {
                            existingSimpleLocalVehicleId:
                                stock.inventory.__typename === 'MobilityInventory' && stock.inventory.variant.id,
                        },
                        languageId: currentLanguageId,
                        stockId: stock.id,

                        /**
                         * for normal drafting gift voucher via mobility stock, we can preset it as 1 day, this will be updated
                         * inside gift voucher kyc step
                         */
                        numberOfBookingReferenceDays: 1,
                    },
                },
            })
            .finally(() => {
                notification.destroy('primary');
            });

        navigate('../giftCode', {
            state: { token: data.draftGiftVoucher.token, stage: JourneyStage.ApplicantKYC },
        });
    }, [
        apolloClient,
        currentLanguageId,
        endpoint.id,
        endpoint.mobilityApplicationModule.giftVoucherModuleId,
        endpoint.mobilityApplicationModule.id,
        navigate,
        notification,
        stock?.id,
        stock?.inventory,
        t,
    ]);
};

export default useSubmitGiftVoucher;
